<template>
  <v-container class="contact-page" fluid>
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">Awards &amp; Certifications <span class="thai">รางวัลและมาตรฐาน</span></h1>
        <p class="subhead mb-0">Manage group-wide and branch-level awards shown on corporate pages.</p>
      </div>
      <v-btn v-if="canAdd" class="setup-btn" color="primary" @click="$router.push({name: 'award-create'})">
        <v-icon left small>mdi-plus</v-icon>
        Create Award
      </v-btn>
    </div>

    <v-card class="table-card">
      <div class="filter-bar">
        <div class="search-wrap">
          <v-text-field v-model="searchText" append-icon="mdi-magnify" outlined dense hide-details label="Search by award title..." clearable @input="onSearch" />
        </div>
        <v-select v-if="isSuperAdmin" v-model="selectedOrg" :items="orgOptionsWithAll" outlined dense hide-details item-text="text" item-value="value" class="small-select" :disabled="isBranchLocked" placeholder="All Branches" @input="onOrgFilterChange" />
        <v-btn outlined class="filter-btn" :class="{'has-filtered': hasAppliedFilters}" @click="handleFilterButtonClick">
          <v-icon small left>{{ hasAppliedFilters ? 'mdi-close' : 'mdi-filter-variant' }}</v-icon>
          {{ hasAppliedFilters ? 'Clear' : 'Filter' }}
        </v-btn>
      </div>

      <v-data-table :loading="isPageLoading" :headers="headers" :items="list" :items-per-page="10" hide-default-footer class="contact-table">
        <template v-slot:item.no="{index, item}">{{ rowNo(index, item) }}</template>
        <template v-slot:item.scope="{item}">
          <span class="scope-pill" :class="{'group': item.org_codename === 'group'}">{{ item.org_codename === 'group' ? 'Group Central' : (item.org_codename || '') }}</span>
        </template>
        <template v-slot:item.title="{item}">
          <div class="title-wrap">
            <strong>{{ item.title_en || item.title_th || '' }}</strong>
            <div class="title-sub">{{ item.title_th || '' }}</div>
          </div>
        </template>
        <template v-slot:item.action="{item}">
          <button v-if="canEdit" class="icon-btn" @click.stop="goToEdit(item)"><v-icon size="16">mdi-pencil-outline</v-icon></button>
          <button v-if="canDelete" class="icon-btn ml-2" @click.stop="remove(item)"><v-icon size="16">mdi-delete-outline</v-icon></button>
        </template>
      </v-data-table>

      <div class="table-foot">Showing 1 to {{ list.length }} of {{ list.length }} entries</div>
    </v-card>
  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'

export default {
  data() {
    return {
      searchText: '',
      selectedOrg: '',
      localLoading: false,
      headers: [
        {text: 'NO.', value: 'no', sortable: false, width: 70},
        {text: 'BRANCH / SCOPE', value: 'scope', sortable: false, width: 220},
        {text: 'AWARD TITLE', value: 'title', sortable: false},
        {text: 'AWARD YEAR', value: 'award_year', sortable: false, width: 160},
        {text: 'ACTIONS', value: 'action', sortable: false, width: 120, align: 'center'}
      ]
    }
  },
  computed: {
    ...mapState('awards', ['list', 'isLoading', 'orgOptions']),
    ...mapState('auth', ['user']),
    canAdd() { return hasPermision('CanAddAward') },
    canEdit() { return hasPermision('CanEditAward') },
    canDelete() { return hasPermision('CanDeleteAward') },
    isBranchLocked() {
      return !this.isSuperAdmin || this.orgOptions.length === 1
    },
    isSuperAdmin() {
      const currentUser = this.user || {}
      return !!currentUser.is_superuser
    },
    orgOptionsWithAll() {
      const branchOptions = Array.isArray(this.orgOptions) ? this.orgOptions.filter(item => item && item.value !== 'group') : []
      return [
        {text: 'All Branches', value: ''},
        {text: 'Group Central', value: 'group'},
        ...branchOptions
      ]
    },
    hasAppliedFilters() {
      const hasSearch = !!(this.searchText || '').trim()
      const hasBranchFilter = this.isSuperAdmin && !!(this.selectedOrg || '').trim()
      return hasSearch || hasBranchFilter
    },
    isPageLoading() {
      return this.localLoading || this.isLoading
    }
  },
  async created() {
    this.localLoading = true
    try {
      this.setResource('awards')
      await this.fetchOrgOptions()
      this.setTextSearch('')
      if (!this.isSuperAdmin) {
        this.selectedOrg = this.getUserOrgCode()
        this.setFilterOrgCode(this.selectedOrg)
      } else if (this.orgOptions.length === 1) {
        this.selectedOrg = this.orgOptions[0].value
        this.setFilterOrgCode(this.selectedOrg)
      } else {
        this.setFilterOrgCode('')
      }
      await this.fetch()
    } finally {
      this.localLoading = false
    }
  },
  methods: {
    ...mapActions('awards', ['setResource', 'fetch', 'setTextSearch', 'setFilterOrgCode', 'fetchOrgOptions', 'deleteById']),
    getUserOrgCode() {
      const user = this.user || {}
      return user.org_code || user.org_codename || user.org || ''
    },
    rowNo(index, item) {
      let rowIndex = Number(index)
      if (Number.isNaN(rowIndex)) {
        const fallbackIndex = (this.list || []).findIndex((listItem) => {
          if (item && item.id && listItem.id) return String(listItem.id) === String(item.id)
          return listItem === item
        })
        rowIndex = fallbackIndex >= 0 ? fallbackIndex : 0
      }
      return rowIndex + 1
    },
    async onSearch() {
      this.localLoading = true
      try {
        this.setTextSearch(this.searchText || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async onOrgFilterChange() {
      this.localLoading = true
      try {
        this.setFilterOrgCode(this.selectedOrg || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async handleFilterButtonClick() {
      if (!this.hasAppliedFilters) return

      this.localLoading = true
      try {
        this.searchText = ''
        this.selectedOrg = this.isSuperAdmin ? '' : this.getUserOrgCode()
        this.setTextSearch('')
        this.setFilterOrgCode(this.selectedOrg)
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    goToEdit(item) {
      this.$router.push({name: 'award-edit', params: {id: item.id}})
    },
    async remove(item) {
      if (!window.confirm('Delete this record?')) return
      this.localLoading = true
      try {
        const ok = await this.deleteById(item.id)
        if (ok) await this.fetch()
      } finally {
        this.localLoading = false
      }
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.thai { color: #64748b; font-size: 15px; font-weight: 500; margin-left: 6px; }
.subhead { color: #64748b; font-size: 13px; }
.setup-btn { text-transform: none; font-weight: 700; border-radius: 10px; }
.table-card { border: 1px solid #e6e9f0; border-radius: 10px; box-shadow: none; overflow: hidden; }
.filter-bar { display: flex; gap: 10px; padding: 14px; border-bottom: 1px solid #edf2fb; }
.search-wrap { flex: 3.5; }
.small-select { flex: 1.5; }
.filter-btn { text-transform: none; font-weight: 600; min-width: 96px; }
.filter-btn.has-filtered { border-color: #f3b5b5 !important; color: #c62828 !important; background: #fdecec; }
.scope-pill { background: #eef4ff; color: #1e49ba; border-radius: 7px; padding: 6px 10px; font-weight: 700; font-size: 12px; }
.scope-pill.group { background: #f1efff; color: #6d57f5; }
.title-wrap { line-height: 1.25; }
.title-sub { color: #7284a5; font-size: 13px; }
.icon-btn { width: 30px; height: 30px; border-radius: 7px; border: 1px solid #e6e9f0; background: #fff; }
.table-foot { padding: 14px 16px; border-top: 1px solid #edf2fb; color: #64748b; font-size: 12px; }
.contact-table::v-deep .v-data-table-header th { color: #7c8aa5 !important; font-size: 12px; font-weight: 700; }
.contact-table::v-deep td { font-size: 14px; }
</style>
